import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Alert,
  StyleSheet,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import axios from "axios";
import { FASTAPI_BASE_URL } from "../../config";
import { useAuth } from "../../AuthContext";
import {
  publishTest,
  unpublishTest,
  addTestShares,
  shareTestWithClass,
  getTeacherClasses,
  listSharedClasses,
  listTestShares,
  parseEmailsFromText,
} from "../../apiTests";

import WizardStep1Details from "./WizardStep1Details";
import WizardStep2Questions from "./WizardStep2Questions";
import WizardStep3Review from "./WizardStep3Review";
import WizardStep4Publish from "./WizardStep4Publish";
import { useTranslation } from "../../i18n/useTranslation";

const qKey = (id) => String(id ?? "");

export default function CreateEditTestWizard({ isDarkMode, editTestId, onComplete }) {
  const c = getColors(isDarkMode);
  const { t } = useTranslation();
  const auth = useAuth();
  const { user, fetchWithAuth } = auth || {};
  const userEmail = user?.email || "Guest";

  const STEPS = [
    { num: 1, title: t("Test Details"), desc: t("Title, schedule, duration") },
    { num: 2, title: t("Add Questions"), desc: t("Bank or AI-generated") },
    { num: 3, title: t("Review & Configure"), desc: t("Points, visibility, sharing") },
    { num: 4, title: t("Publish"), desc: t("Final review & go live") },
  ];

  /* ------------------------------------------------------------------ */
  /*  Wizard state                                                       */
  /* ------------------------------------------------------------------ */
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState("");

  /* ------------------------------------------------------------------ */
  /*  Test details (Step 1)                                              */
  /* ------------------------------------------------------------------ */
  const [testDetails, setTestDetails] = useState({
    title: "",
    description: "",
    chapters: [],
    start_time: "",
    end_time: "",
    duration_minutes: "",
    passing_score: "60",
    max_attempts: "1",
    is_published: false,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedTest, setSelectedTest] = useState(null);

  /* ------------------------------------------------------------------ */
  /*  Curriculum / Topic tree (Step 2 - bank path)                       */
  /* ------------------------------------------------------------------ */
  const [curriculum, setCurriculum] = useState([]);
  const [gradeFilter, setGradeFilter] = useState(new Set());
  const [subjectFilter, setSubjectFilter] = useState(new Set());
  const [selectedTopics, setSelectedTopics] = useState(new Set());

  const levels = useMemo(() => (Array.isArray(curriculum) ? curriculum : []), [curriculum]);
  const subjectsByLevel = useMemo(() => {
    const m = new Map();
    for (const lvl of levels) m.set(lvl.id, lvl.subjects || []);
    return m;
  }, [levels]);

  /* ------------------------------------------------------------------ */
  /*  Questions (Step 2)                                                 */
  /* ------------------------------------------------------------------ */
  const [quizData, setQuizData] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(new Set());

  /* ------------------------------------------------------------------ */
  /*  AI generation state (Step 2 - AI path)                             */
  /* ------------------------------------------------------------------ */
  const [aiState, setAiState] = useState({
    grade: "",
    difficulty: "mixed",
    instructions: "",
    mcq: "5",
    fill: "3",
    trueFalse: "2",
    shortEssay: "2",
    longEssay: "1",
    assoc: "0",
  });

  /* ------------------------------------------------------------------ */
  /*  Review / Config (Step 3)                                           */
  /* ------------------------------------------------------------------ */
  const [pointValues, setPointValues] = useState({});
  const [visibility, setVisibility] = useState("private");
  const [sharedClasses, setSharedClasses] = useState([]);
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState(false);
  const [shareEmails, setShareEmails] = useState("");

  /* ------------------------------------------------------------------ */
  /*  Fetch curriculum on mount                                          */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const fetchCurriculum = async () => {
      try {
        const response = await axios.get(`${FASTAPI_BASE_URL}/curriculum`);
        setCurriculum(response.data);
      } catch (error) {
        console.error("Error fetching curriculum:", error);
      }
    };
    fetchCurriculum();
  }, []);

  /* ------------------------------------------------------------------ */
  /*  Fetch teacher classes on mount                                     */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    async function fetchClasses() {
      setClassesLoading(true);
      setClassesError(false);
      try {
        const classes = await getTeacherClasses(userEmail);
        setTeacherClasses(classes || []);
      } catch (err) {
        console.warn("Failed to fetch teacher classes", err);
        setClassesError(true);
      } finally {
        setClassesLoading(false);
      }
    }
    if (userEmail && userEmail !== "Guest") fetchClasses();
  }, [userEmail]);

  /* ------------------------------------------------------------------ */
  /*  Load existing test if editTestId provided                          */
  /* ------------------------------------------------------------------ */
  const questionsLoadedRef = useRef(false);

  useEffect(() => {
    if (!editTestId) return;
    questionsLoadedRef.current = false;
    const loadTestMetadata = async () => {
      setLoading(true);
      try {
        // Fetch test details (metadata only — fast)
        const res = await axios.get(`${FASTAPI_BASE_URL}/tests/${editTestId}`);
        const td = res?.data?.test || {};
        setTestDetails({
          title: td.title || "",
          description: td.description || "",
          chapters: td.chapters || [],
          start_time: td.start_time || "",
          end_time: td.end_time || "",
          duration_minutes: td.duration_minutes ?? "",
          passing_score: td.passing_score ?? "60",
          max_attempts: td.max_attempts ?? "1",
          is_published: !!td.is_published,
        });
        setSelectedTest({ id: editTestId, ...td });

        // Restore visibility from published_scope ('public' | 'org') when published;
        // otherwise treat as private. Without this, edit always resets to "private".
        if (td.is_published) {
          const scope = String(td.published_scope || "").toLowerCase();
          if (scope === "public") setVisibility("public");
          else if (scope === "org") setVisibility("organization");
          else setVisibility("private");
        } else {
          setVisibility("private");
        }

        // Load existing shared emails and shared classes so the teacher
        // can see who already has access (and isn't surprised by them
        // vanishing on edit).
        const [sharesResult, classesResult] = await Promise.allSettled([
          listTestShares(editTestId),
          fetchWithAuth
            ? listSharedClasses(editTestId, fetchWithAuth)
            : Promise.resolve([]),
        ]);

        if (sharesResult.status === "fulfilled") {
          const emails = (sharesResult.value || [])
            .map((s) => s?.student_email)
            .filter(Boolean);
          setShareEmails(emails.join(", "));
        } else {
          console.warn("Failed to load existing test shares:", sharesResult.reason);
        }

        if (classesResult.status === "fulfilled") {
          setSharedClasses(classesResult.value || []);
        } else {
          console.warn("Failed to load shared classes:", classesResult.reason);
        }
      } catch (error) {
        console.error("Error loading test for edit:", error);
      } finally {
        setLoading(false);
      }
    };
    loadTestMetadata();
  }, [editTestId, fetchWithAuth]);

  /* ------------------------------------------------------------------ */
  /*  Lazy-load questions when navigating to Step 2+                     */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!editTestId || currentStep < 2 || questionsLoadedRef.current) return;
    questionsLoadedRef.current = true;
    const loadQuestions = async () => {
      setLoading(true);
      try {
        const qRes = await axios.get(`${FASTAPI_BASE_URL}/tests/${editTestId}/questions`);
        const parsedQuiz = qRes.data.quiz || [];

        // BDD 08/25/2026 - preserve official external question identity
        const modifiedQuiz = parsedQuiz.map((q) => {
          let question_id;

          if (q.external_question_id) {
            question_id = q.external_question_id;
          } else if (
            q.question_bank_id != null &&
            typeof q.question_bank_id === "number" &&
            q.question_bank_id >= 0
          ) {
            question_id = q.question_bank_id;
          } else if (q.ai_question_uuid) {
            question_id = q.ai_question_uuid;
          } else {
            question_id = `temp-${Math.random().toString(36).substring(2, 8)}`;
          }

          return { ...q, question_id };
        });

        setQuizData(modifiedQuiz);

        // Pre-select all existing questions
        const preselected = new Set(modifiedQuiz.map((q) => qKey(q.question_id)));
        setSelectedQuestionIds(preselected);

        // BDD 08/25/2026 - display official marks for imported exam questions
        const pts = {};
        modifiedQuiz.forEach((q) => {
          if (q.external_question_id && q.official_points != null) {
            pts[qKey(q.question_id)] = Number(q.official_points);
          } else {
            pts[qKey(q.question_id)] = q.score ?? 5;
          }
        });
        setPointValues(pts);

      } catch (error) {
        console.error("Error loading test questions:", error);
      } finally {
        setLoading(false);
      }
    };
    loadQuestions();
  }, [editTestId, currentStep]);

  /* ------------------------------------------------------------------ */
  /*  Pre-select first grade when curriculum loads                       */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!levels.length || gradeFilter.size) return;
    const firstWithSubjects = levels.find((l) => Array.isArray(l.subjects) && l.subjects.length);
    const g = firstWithSubjects || levels[0];
    if (g) {
      setGradeFilter(new Set([g.id]));
      const firstSubject = (g.subjects || [])[0];
      if (firstSubject) setSubjectFilter(new Set([firstSubject.id]));
    }
  }, [levels]);

  /* ------------------------------------------------------------------ */
  /*  Validation                                                         */
  /* ------------------------------------------------------------------ */
  const validateStep1 = useCallback(() => {
    const errors = {};
    if (!testDetails.title || testDetails.title.trim() === "") {
      errors.title = t("Test title is required.");
    }
    if (!testDetails.start_time || !testDetails.end_time) {
      errors.schedule = t("Both start and end times must be provided.");
    } else {
      const start = new Date(testDetails.start_time);
      const end = new Date(testDetails.end_time);
      if (end <= start) {
        errors.schedule = t("End time must be greater than start time.");
      }
    }
    if (!testDetails.duration_minutes || parseInt(testDetails.duration_minutes, 10) <= 0) {
      errors.duration = t("Test duration must be greater than zero.");
    }
    return errors;
  }, [testDetails, t]);

  /* ------------------------------------------------------------------ */
  /*  Navigation                                                         */
  /* ------------------------------------------------------------------ */
  const goToStep = useCallback(
    (step) => {
      if (step < 1 || step > 4) return;

      // Validate step 1 before proceeding
      if (currentStep === 1 && step > 1) {
        const errors = validateStep1();
        setValidationErrors(errors);
        if (Object.keys(errors).length > 0) return;
      }

      // Warn if no questions selected when going to step 3+
      if (currentStep === 2 && step > 2 && selectedQuestionIds.size === 0) {
        Alert.alert(t("No Questions"), t("Please select at least one question before continuing."));
        return;
      }

      setCurrentStep(step);
    },
    [currentStep, validateStep1, selectedQuestionIds.size, t]
  );

  /* ------------------------------------------------------------------ */
  /*  Topic toggle                                                       */
  /* ------------------------------------------------------------------ */
  const toggleTopic = useCallback((chapterId, topicId) => {
    const key = `${chapterId}-${topicId}`;
    setSelectedTopics((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const toggleGrade = useCallback((id) => {
    setGradeFilter((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleSubject = useCallback((id) => {
    setSubjectFilter((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  /* ------------------------------------------------------------------ */
  /*  Fetch questions from bank                                          */
  /* ------------------------------------------------------------------ */
  const fetchQuestions = useCallback(async () => {
    if (selectedTopics.size === 0) {
      Alert.alert(t("No Topics"), t("Please select at least one topic."));
      return;
    }

    const selectedPairs = Array.from(selectedTopics).map((key) => {
      const [chapterId, topicId] = key.split("-");
      return { chapter_id: parseInt(chapterId, 10), topic_id: parseInt(topicId, 10) };
    });

    setLoading(true);
    try {
      const response = await axios.post(`${FASTAPI_BASE_URL}/question-bank/fetch_New2`, {
        pairs: selectedPairs,
        language_id: 1,
      });

      const raw = Array.isArray(response?.data?.quiz) ? response.data.quiz : [];
      const parsedQuiz = raw.map((question) => ({
        ...question,
        options: {
          correct_answer: question.options?.correct_answer,
          option1: question.options?.option1,
          option2: question.options?.option2,
          option3: question.options?.option3,
          option4: question.options?.option4,
        },
        hints: {
          hint1: question.hints?.hint1,
          hint2: question.hints?.hint2,
        },
        solution: question.solution,
        difficulty_level: question.difficulty_level,
        difficulty: question.difficulty,
      }));

      // Dedupe with existing pool
      setQuizData((prev) => {
        const merged = [...prev, ...parsedQuiz];
        const dedupedMap = new Map();
        merged.forEach((q) => dedupedMap.set(String(q.question_id), q));
        return Array.from(dedupedMap.values());
      });
    } catch (error) {
      console.error("Error fetching questions:", error);
      Alert.alert(t("Error"), t("Failed to fetch questions from the bank."));
    } finally {
      setLoading(false);
    }
  }, [selectedTopics, t]);

  /* ------------------------------------------------------------------ */
  /*  Toggle question selection                                          */
  /* ------------------------------------------------------------------ */
  const toggleQuestionPick = useCallback((qid) => {
    const k = qKey(qid);
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  }, []);

  const selectAllQuestions = useCallback((visible) => {
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      for (const q of visible) next.add(qKey(q.question_id));
      return next;
    });
  }, []);

  const clearAllQuestions = useCallback(() => {
    setSelectedQuestionIds(new Set());
  }, []);

  /* ------------------------------------------------------------------ */
  /*  AI generation                                                      */
  /* ------------------------------------------------------------------ */
  const updateAI = useCallback((field, value) => {
    setAiState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const generateAI = useCallback(async () => {
    const selectedPairs = Array.from(selectedTopics).map((key) => {
      const [chapterId, topicId] = key.split("-");
      return { chapter_id: parseInt(chapterId, 10), topic_id: parseInt(topicId, 10) };
    });

    setLoading(true);
    try {
      const payload = {
        subject_id: 1,
        subject_name: "Science",
        language_id: 1,
        chapter_topic_pairs: selectedPairs,
        grade_level: parseInt(aiState.grade, 10) || 7,
        difficulty: aiState.difficulty || "mixed",
        num_mcq: parseInt(aiState.mcq, 10) || 0,
        num_fill_in_the_blank: parseInt(aiState.fill, 10) || 0,
        num_word_assoc: parseInt(aiState.assoc, 10) || 0,
        num_short_essay: parseInt(aiState.shortEssay, 10) || 0,
        num_long_essay: parseInt(aiState.longEssay, 10) || 0,
        instructions: aiState.instructions || "",
      };

      const response = await axios.post(`${FASTAPI_BASE_URL}/teacher/generate-ai-test`, payload);
      const rawAiQuestions = response.data.questions || [];
      // Tag AI questions with requested difficulty (backend doesn't include it)
      const reqDifficulty = aiState.difficulty || "mixed";
      const aiQuestions = rawAiQuestions.map((q) => ({
        ...q,
        difficulty: q.difficulty || reqDifficulty,
        question_source: "ai",
      }));

      setQuizData((prev) => {
        const merged = [...prev, ...aiQuestions];
        const dedupedMap = new Map();
        merged.forEach((q) => dedupedMap.set(String(q.question_id), q));
        return Array.from(dedupedMap.values());
      });

      // Auto-select AI-generated questions
      setSelectedQuestionIds((prev) => {
        const next = new Set(prev);
        for (const q of aiQuestions) next.add(qKey(q.question_id));
        return next;
      });
    } catch (error) {
      console.error("AI Test Generation Error:", error);
      Alert.alert(t("Error"), t("Failed to generate questions. Check console for details."));
    } finally {
      setLoading(false);
    }
  }, [aiState, selectedTopics, t]);

  /* ------------------------------------------------------------------ */
  /*  Point values                                                       */
  /* ------------------------------------------------------------------ */
  const updatePoints = useCallback((questionKey, value) => {
    setPointValues((prev) => ({ ...prev, [questionKey]: value }));
  }, []);

  /* ------------------------------------------------------------------ */
  /*  Sharing                                                            */
  /* ------------------------------------------------------------------ */
  const handleShareClass = useCallback(
    async (classId) => {
      if (!selectedTest?.id) {
        // Will share after save
        setSharedClasses((prev) => [...prev, { id: classId }]);
        return;
      }
      try {
        await shareTestWithClass(selectedTest.id, classId);
        setSharedClasses((prev) => [...prev, { id: classId }]);
      } catch (err) {
        console.error("Failed to share with class:", err);
      }
    },
    [selectedTest]
  );

  const handleRemoveClass = useCallback((classId) => {
    setSharedClasses((prev) => prev.filter((sc) => String(sc.id || sc.class_id) !== String(classId)));
  }, []);

  /* ------------------------------------------------------------------ */
  /*  Save / Publish                                                     */
  /* ------------------------------------------------------------------ */
  // BDD 08/25/2026 - preserve official external question metadata
  const addSelectedQuestionsToTest = useCallback(
    async (testId) => {
      const selected = quizData.filter((q) =>
        selectedQuestionIds.has(qKey(q.question_id))
      );

      for (const question of selected) {
        let question_bank_id = -2;
        let ai_question_uuid = null;
        let external_question_id = question.external_question_id || null;
        let question_source = "manual";

        if (external_question_id) {
          // Official/imported external question
          question_bank_id = null;
          ai_question_uuid = null;
          question_source =
            question.question_source || "lebanon_official_exam";
        } else if (!isNaN(Number(question.question_id))) {
          // Existing Saras question-bank question
          question_bank_id = Number(question.question_id);
          question_source = "manual";
        } else {
          // AI-generated question
          ai_question_uuid =
            question.ai_question_uuid || question.question_id;
          question_source = question.question_source || "ai";
        }

        const isOfficialQuestion =
          !!question.external_question_id &&
          question.official_points != null;

        const configuredPoints =
          pointValues[qKey(question.question_id)];

        const payload = {
          question_bank_id,
          ai_question_uuid,
          external_question_id,

          question_text:
            typeof question.question === "string"
              ? question.question
              : JSON.stringify(question.question),

          question_type:
            question.question_type != null
              ? question.question_type.toString()
              : "",

          options:
            question.options
              ? JSON.stringify(question.options)
              : null,

          correct_answer:
            question.correct_answer != null
              ? String(question.correct_answer)
              : question.options?.correct_ans != null
                ? String(question.options.correct_ans)
                : null,

          // Keep legacy Saras runtime score integer for official questions.
          score: isOfficialQuestion
            ? (question.score ?? 1)
            : parseInt(configuredPoints ?? question.score ?? 5, 10),

          // Preserve the exact decimal official examination mark.
          official_points: isOfficialQuestion
            ? Number(configuredPoints ?? question.official_points)
            : null,

          difficulty_level: question.difficulty_level,
          difficulty: question.difficulty,

          order: question.order ?? 0,

          hints:
            question.hints
              ? JSON.stringify(question.hints)
              : null,

          solution:
            question.solution
              ? JSON.stringify(question.solution)
              : null,

          question_source,

          language_id: question.language_id ?? 1,
        };

        await axios.post(
          `${FASTAPI_BASE_URL}/tests/${testId}/questions`,
          payload,
          {
            headers: { "Content-Type": "application/json" },
          }
        );
      }
    },
    [quizData, selectedQuestionIds, pointValues]
  );


  const handleSaveDraft = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        teacher_email: userEmail,
        title: testDetails.title,
        description: testDetails.description,
        chapters: testDetails.chapters,
        start_time: testDetails.start_time ? new Date(testDetails.start_time).toISOString() : null,
        end_time: testDetails.end_time ? new Date(testDetails.end_time).toISOString() : null,
        duration_minutes: parseInt(testDetails.duration_minutes, 10) || 0,
        is_published: false,
      };

      let res;
      if (selectedTest?.id) {
        res = await axios.patch(`${FASTAPI_BASE_URL}/tests/${selectedTest.id}`, payload);
        await axios.delete(`${FASTAPI_BASE_URL}/tests/${selectedTest.id}/questions`);
      } else {
        res = await axios.post(`${FASTAPI_BASE_URL}/tests`, payload);
        setSelectedTest(res.data);
      }

      const testId = res.data.id || selectedTest?.id;
      if (testId && selectedQuestionIds.size > 0) {
        await addSelectedQuestionsToTest(testId);
      }

      // Share with classes if pending
      for (const sc of sharedClasses) {
        try {
          await shareTestWithClass(testId, sc.id || sc.class_id);
        } catch (e) {
          console.warn("Failed to share class:", e);
        }
      }

      // Share with emails if any
      const emails = parseEmailsFromText(shareEmails);
      if (emails.length && testId) {
        try {
          await addTestShares(testId, emails);
        } catch (e) {
          const detail = e?.response?.data?.detail || e.message || t("Unknown error");
          Alert.alert(
            t("Share Warning"),
            t("Test saved but could not share with emails: {detail}", { detail })
          );
        }
      }

      Alert.alert(t("Saved"), t("Test saved as draft successfully."));
    } catch (error) {
      console.error("Error saving test:", error);
      Alert.alert(t("Error"), t("Failed to save test."));
    } finally {
      setSaving(false);
    }
  }, [testDetails, selectedTest, selectedQuestionIds, userEmail, addSelectedQuestionsToTest, sharedClasses, shareEmails, t]);

  const handlePublish = useCallback(async () => {
    setPublishError("");

    // Pre-flight validation: a "Private" test isn't visible to anyone unless
    // it's been shared with at least one class or specific student email.
    // Block the publish action up-front with a clear, in-UI message instead
    // of letting the test save into an unreachable state.
    if (visibility === "private") {
      const emails = parseEmailsFromText(shareEmails);
      const hasShares = sharedClasses.length > 0 || emails.length > 0;
      if (!hasShares) {
        setPublishError(
          t(
            "Private tests cannot be published without sharing. Please change visibility to Public or Organization, share with at least one class or student email, or save as draft."
          )
        );
        return;
      }
    }

    setPublishing(true);
    try {
      // Save first if not saved
      const payload = {
        teacher_email: userEmail,
        title: testDetails.title,
        description: testDetails.description,
        chapters: testDetails.chapters,
        start_time: testDetails.start_time ? new Date(testDetails.start_time).toISOString() : null,
        end_time: testDetails.end_time ? new Date(testDetails.end_time).toISOString() : null,
        duration_minutes: parseInt(testDetails.duration_minutes, 10) || 0,
        is_published: true,
      };

      let res;
      if (selectedTest?.id) {
        res = await axios.patch(`${FASTAPI_BASE_URL}/tests/${selectedTest.id}`, payload);
        await axios.delete(`${FASTAPI_BASE_URL}/tests/${selectedTest.id}/questions`);
      } else {
        res = await axios.post(`${FASTAPI_BASE_URL}/tests`, payload);
        setSelectedTest(res.data);
      }

      const testId = res.data.id || selectedTest?.id;
      if (testId && selectedQuestionIds.size > 0) {
        await addSelectedQuestionsToTest(testId);
      }

      // Publish (skip for private — email-shared tests are visible without publishing)
      const scope = visibility === "public" ? "public" : visibility === "organization" ? "org" : null;
      if (scope) {
        await publishTest(testId, scope);
      }

      // Share with classes
      const classShareErrors = [];
      for (const sc of sharedClasses) {
        try {
          await shareTestWithClass(testId, sc.id || sc.class_id);
        } catch (e) {
          const detail = e?.response?.data?.detail || e.message || t("Unknown error");
          console.warn("Failed to share class:", e);
          classShareErrors.push(detail);
        }
      }

      // Share with emails
      const emails = parseEmailsFromText(shareEmails);
      let emailShareError = "";
      if (emails.length && testId) {
        try {
          await addTestShares(testId, emails);
        } catch (e) {
          emailShareError = e?.response?.data?.detail || e.message || t("Unknown error");
        }
      }

      if (classShareErrors.length || emailShareError) {
        const parts = [];
        if (classShareErrors.length) {
          parts.push(t("Class share failed: {detail}", { detail: classShareErrors.join("; ") }));
        }
        if (emailShareError) {
          parts.push(t("Email share failed: {detail}", { detail: emailShareError }));
        }
        setPublishError(t("Test saved, but some shares failed. {details}", { details: parts.join(" | ") }));
        Alert.alert(t("Share Warning"), parts.join("\n"));
        return;
      }

      Alert.alert(t("Published"), t("Test published successfully!"));
      if (onComplete) onComplete();
    } catch (error) {
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        t("Unknown error");
      console.error("Error publishing test:", error);
      setPublishError(t("Failed to publish test. {detail}", { detail }));
      Alert.alert(t("Error"), t("Failed to publish test. {detail}", { detail }));
    } finally {
      setPublishing(false);
    }
  }, [
    testDetails,
    selectedTest,
    selectedQuestionIds,
    visibility,
    userEmail,
    addSelectedQuestionsToTest,
    sharedClasses,
    shareEmails,
    onComplete,
    t,
  ]);

  /* ------------------------------------------------------------------ */
  /*  Computed values for Step 4                                          */
  /* ------------------------------------------------------------------ */
  const selectedQuestions = useMemo(
    () => quizData.filter((q) => selectedQuestionIds.has(qKey(q.question_id))),
    [quizData, selectedQuestionIds]
  );

  // BDD 08/25/2026 - support decimal official examination marks
  const totalPoints = useMemo(() => {
    return selectedQuestions.reduce((sum, q) => {
      const pts = Number(
        pointValues[qKey(q.question_id)] ??
        q.official_points ??
        q.score ??
        5
      );

      return sum + (Number.isFinite(pts) ? pts : 0);
    }, 0);
  }, [selectedQuestions, pointValues]);

  /* ------------------------------------------------------------------ */
  /*  Render                                                             */
  /* ------------------------------------------------------------------ */
  const selectedCount = selectedQuestionIds.size;

  return (
    <View style={[styles.wizardCard, { backgroundColor: c.cardBg, borderColor: c.border }]}>
    <View style={styles.wizard}>
      {/* Sidebar - Step Navigation */}
      <View style={[styles.wizardSidebar]}>
        <View style={styles.stepNav}>
          {STEPS.map((step, idx) => {
            const isCompleted = step.num < currentStep;
            const isActive = step.num === currentStep;
            const isLast = idx === STEPS.length - 1;

            return (
              <TouchableOpacity
                key={step.num}
                style={styles.stepItem}
                onPress={() => goToStep(step.num)}
                activeOpacity={0.7}
              >
                {/* Connecting line */}
                {!isLast && (
                  <View
                    style={[
                      styles.stepLine,
                      {
                        backgroundColor: isCompleted ? c.teal : c.border,
                      },
                    ]}
                  />
                )}

                {/* Dot */}
                <View
                  style={[
                    styles.stepDot,
                    {
                      borderColor: isActive ? c.accent : isCompleted ? c.teal : c.border,
                      backgroundColor: isActive ? c.accent : isCompleted ? c.teal : c.cardBg,
                    },
                    isActive && {
                      shadowColor: c.accent,
                      shadowOffset: { width: 0, height: 0 },
                      shadowOpacity: 0.3,
                      shadowRadius: 4,
                    },
                  ]}
                >
                  {isCompleted ? (
                    <MaterialCommunityIcons name="check" size={16} color="#fff" />
                  ) : (
                    <Text
                      style={[
                        styles.stepDotText,
                        { color: isActive ? "#fff" : c.textMuted },
                      ]}
                    >
                      {step.num}
                    </Text>
                  )}
                </View>

                {/* Text */}
                <View style={styles.stepText}>
                  <Text
                    style={[
                      styles.stepTitle,
                      {
                        color: isActive
                          ? c.textPrimary
                          : isCompleted
                          ? c.tealText
                          : c.textMuted,
                      },
                    ]}
                  >
                    {step.title}
                  </Text>
                  <Text style={[styles.stepDesc, { color: c.textMuted }]}>{step.desc}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Sidebar action buttons — visible on Step 2 (Add Questions) */}
        {currentStep === 2 && (
          <View style={[styles.sidebarActions, { borderTopColor: c.border }]}>
            {/* Selection summary */}
            {selectedCount > 0 && (
              <View style={styles.sidebarSelectionSummary}>
                <MaterialCommunityIcons name="checkbox-multiple-marked" size={16} color={c.teal} />
                <Text style={[styles.sidebarSelectionText, { color: c.textSecondary }]}>
                  <Text style={{ color: c.teal, fontWeight: "700" }}>{selectedCount}</Text>
                  {" "}{selectedCount !== 1 ? t("questions selected") : t("question selected")}
                </Text>
              </View>
            )}

            <TouchableOpacity
              style={[styles.sidebarBtnPrimary, { backgroundColor: c.accent }]}
              onPress={() => goToStep(3)}
              activeOpacity={0.7}
            >
              <Text style={styles.sidebarBtnPrimaryText}>
                {t("Review Selected")} ({selectedCount})
              </Text>
              <MaterialCommunityIcons name="chevron-right" size={16} color="#fff" />
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.sidebarBtnSecondary, { borderColor: c.border, backgroundColor: c.cardBg }]}
              onPress={() => goToStep(1)}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons name="chevron-left" size={16} color={c.textPrimary} />
              <Text style={[styles.sidebarBtnSecondaryText, { color: c.textPrimary }]}>{t("Back")}</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Main content */}
      <View style={styles.wizardMain}>
        {currentStep === 1 && (
          <WizardStep1Details
            isDarkMode={isDarkMode}
            testDetails={testDetails}
            onUpdateDetails={setTestDetails}
            onNext={() => goToStep(2)}
            validationErrors={validationErrors}
          />
        )}

        {currentStep === 2 && (
          <WizardStep2Questions
            isDarkMode={isDarkMode}
            quizData={quizData}
            selectedQuestionIds={selectedQuestionIds}
            onToggleQuestion={toggleQuestionPick}
            onSelectAll={selectAllQuestions}
            onClearAll={clearAllQuestions}
            onFetchQuestions={fetchQuestions}
            onGenerateAI={generateAI}
            onNext={() => goToStep(3)}
            onBack={() => goToStep(1)}
            levels={levels}
            gradeFilter={gradeFilter}
            subjectFilter={subjectFilter}
            selectedTopics={selectedTopics}
            onToggleGrade={toggleGrade}
            onToggleSubject={toggleSubject}
            onToggleTopic={toggleTopic}
            aiState={aiState}
            onUpdateAI={updateAI}
            loading={loading}
          />
        )}

        {currentStep === 3 && (
          <WizardStep3Review
            isDarkMode={isDarkMode}
            questions={quizData}
            selectedQuestionIds={selectedQuestionIds}
            testDetails={testDetails}
            pointValues={pointValues}
            onUpdatePoints={updatePoints}
            visibility={visibility}
            onUpdateVisibility={setVisibility}
            sharedClasses={sharedClasses}
            onShareClass={handleShareClass}
            onRemoveClass={handleRemoveClass}
            teacherClasses={teacherClasses}
            classesLoading={classesLoading}
            classesError={classesError}
            shareEmails={shareEmails}
            onUpdateShareEmails={setShareEmails}
            onSaveDraft={handleSaveDraft}
            onNext={() => goToStep(4)}
            onBack={() => goToStep(2)}
            saving={saving}
          />
        )}

        {currentStep === 4 && (
          <WizardStep4Publish
            isDarkMode={isDarkMode}
            testDetails={testDetails}
            questionCount={selectedQuestionIds.size}
            totalPoints={totalPoints}
            studentCount={0}
            visibility={visibility}
            sharedClassCount={sharedClasses.length}
            onBack={() => {
              setPublishError("");
              goToStep(3);
            }}
            onPublish={handlePublish}
            publishing={publishing}
            publishError={publishError}
            onDismissError={() => setPublishError("")}
          />
        )}
      </View>
    </View>
    </View>
  );
}

function getColors(isDarkMode) {
  if (isDarkMode) {
    return {
      textPrimary: "#f1f5f9",
      textSecondary: "#94a3b8",
      textMuted: "#64748b",
      border: "#334155",
      cardBg: "#1e293b",
      accent: "#818cf8",
      accentLight: "rgba(99,102,241,0.12)",
      teal: "#2dd4bf",
      tealText: "#5eead4",
    };
  }
  return {
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    border: "#e2e8f0",
    cardBg: "#ffffff",
    accent: "#6366f1",
    accentLight: "#eef2ff",
    teal: "#0d9488",
    tealText: "#115e59",
  };
}

const styles = StyleSheet.create({
  wizardCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 32,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  wizard: {
    flexDirection: "row",
    gap: 32,
    maxHeight: "calc(100vh - 120px)",
  },
  wizardSidebar: {
    width: 240,
    flexShrink: 0,
    alignSelf: "flex-start",
    position: "sticky",
    top: 24,
  },
  wizardMain: {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
  },

  /* Step nav */
  stepNav: {},
  stepItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 14,
    paddingVertical: 14,
    position: "relative",
  },
  stepLine: {
    position: "absolute",
    left: 17,
    top: 48,
    bottom: 0,
    width: 2,
  },
  stepDot: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    zIndex: 1,
  },
  stepDotText: {
    fontSize: 13,
    fontWeight: "700",
  },
  stepText: {},
  stepTitle: {
    fontSize: 14,
    fontWeight: "600",
  },
  stepDesc: {
    fontSize: 11,
    marginTop: 2,
  },

  /* Sidebar action buttons for Step 2 */
  sidebarActions: {
    marginTop: 24,
    paddingTop: 20,
    borderTopWidth: 1,
    gap: 10,
  },
  sidebarSelectionSummary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 4,
  },
  sidebarSelectionText: {
    fontSize: 13,
  },
  sidebarBtnPrimary: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 11,
    paddingHorizontal: 16,
    borderRadius: 10,
    shadowColor: "rgba(99,102,241,0.25)",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 3,
  },
  sidebarBtnPrimaryText: { color: "#fff", fontSize: 13, fontWeight: "600" },
  sidebarBtnSecondary: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 11,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
  },
  sidebarBtnSecondaryText: { fontSize: 13, fontWeight: "600" },
});
